/**
 * hull_look — lit war hulls that read as KILOMETRES (report §9 R6).
 *
 * The war's hulls were baked UNLIT (`MeshBasicMaterial({map})` — flat stickers with no terminator, no specular,
 * no scale cue). Since the one-sun fix every lit material gets a real key, so the war wears PBR under it:
 *  - `litWarMaterial` — the cooked GLB's own maps on a MeshStandardNodeMaterial, plus an EMISSIVE layer:
 *    a civ-coloured FRESNEL RIM and a procedural WINDOW / running-light field in hull metres — triplanar
 *    rows and cells on the dominant face (space_look.hullWindowMask is the CPU mirror), lit by a per-cell
 *    hash, only on the albedo's DARK panels, never on the stern face (the plumes live there), and filtered
 *    toward its mean duty where a cell shrinks under a pixel (no shimmer at 20 km).
 *  - `navPoints` — a few hundred HDR points sampled once per master over the hull surface (MeshSurfaceSampler):
 *    port red / starboard green, white 1 Hz strobes, amber running lights; drawn as clip-space quads with a
 *    pixel floor (a 3 m lamp at 20 km is still a point), one child mesh per capital sharing the side's
 *    geometry + material.
 * The unlit bake stays behind `localStorage atlas:space-unlit = 1` (SpaceModule).
 *
 * Minted materials are cached per (source material, tint, scale) — ten capitals of a side share one set —
 * and handed to the caller's deactivate-disposal list.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec3, vec4, float, int, floor, fract, sin, dot, abs, max, pow, smoothstep, fwidth, mix, select,
  positionGeometry, normalGeometry, normalView, positionViewDirection, texture, uv, attribute, cameraProjectionMatrix,
  modelViewMatrix, screenSize, time, length, clamp,
} from 'three/tsl';
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { WINDOW, WINDOW_EMISSION } from './space_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export const hullLookState = { materials: 0, navMasters: 0, navPoints: 0 };

/** The shader's sin-fract hash (sign-safe — three's hash() saturates negatives; the mirror is space_look.hash2). */
const hash2 = (a: N, b: N): N => fract(sin(a.mul(127.1).add(b.mul(311.7))).mul(43758.5453));

/**
 * One lit material for a war-hull mesh. `metersPerUnit` scales the mesh's geometry units to hull metres;
 * `sternDir` is the stern (+Z of the nose −Z frame) in the mesh's GEOMETRY space; `tint` the civ colour.
 */
export function litWarMaterial(src: THREE.Material, opts: { tint: number; metersPerUnit: number; sternDir: THREE.Vector3; seed: number; windows: boolean }): THREE.Material {
  const s = src as THREE.MeshStandardMaterial;
  const mat = new THREE.MeshStandardNodeMaterial({
    map: s.map ?? null,
    normalMap: s.normalMap ?? null,
    roughnessMap: s.roughnessMap ?? null,
    metalnessMap: s.metalnessMap ?? null,
    aoMap: s.aoMap ?? null,
    color: s.color ? s.color.clone() : new THREE.Color(0x8a94a8),
    roughness: s.roughness ?? 0.6,
    metalness: s.metalness ?? 0.4,
  });
  if (s.normalMap && s.normalScale) mat.normalScale = s.normalScale.clone();
  mat.name = `space-war-hull:${opts.tint.toString(16)}`;
  const uTint = uniform(new THREE.Color(opts.tint));
  const uMpu = uniform(opts.metersPerUnit);
  const uStern = uniform(opts.sternDir.clone().normalize());
  const uSeed = uniform(opts.seed);
  const uWin = uniform(opts.windows ? 1 : 0);
  mat.emissiveNode = Fn(() => {
    // The fresnel rim in the civ colour (a thin bright edge — capitals separate from the black).
    const facing = abs(dot(normalView, positionViewDirection));
    const rim = pow(float(1).sub(facing), float(3.5)).mul(0.55);
    // The window field (hullWindowMask's mirror) in hull metres on the dominant face.
    const p = positionGeometry.mul(uMpu);
    const n = normalGeometry;
    const ax = abs(n.x), ay = abs(n.y), az = abs(n.z);
    const isX = ax.greaterThanEqual(ay).and(ax.greaterThanEqual(az));
    const isY = isX.not().and(ay.greaterThanEqual(az));
    const u = select(isX, p.z, p.x);
    const v = select(isY, p.z, p.y);
    const axisId = select(isX, float(0), select(isY, float(1), float(2)));
    const fv = v.div(WINDOW.rowM), fu = u.div(WINDOW.cellM);
    const row = floor(fv), cell = floor(fu);
    const bandV = smoothstep(WINDOW.band0 - 0.04, WINDOW.band0, fract(fv)).mul(float(1).sub(smoothstep(WINDOW.band1, WINDOW.band1 + 0.04, fract(fv))));
    const bandU = smoothstep(WINDOW.gap0 - 0.05, WINDOW.gap0, fract(fu)).mul(float(1).sub(smoothstep(WINDOW.gap1, WINDOW.gap1 + 0.05, fract(fu))));
    const lit = float(1).sub(smoothstep(WINDOW.occupancy - 0.01, WINDOW.occupancy + 0.01, hash2(row.add(uSeed.mul(17)), cell.add(axisId.mul(101)))));
    const cellMask = bandV.mul(bandU).mul(lit);
    // Far away a cell falls under a pixel: blend toward the mean duty (a lit texture, not shimmer).
    const duty = float((WINDOW.band1 - WINDOW.band0) * (WINDOW.gap1 - WINDOW.gap0) * WINDOW.occupancy);
    const fw = max(fwidth(fu), fwidth(fv));
    const filtered = mix(cellMask, duty, smoothstep(0.25, 0.7, fw));
    // Only the albedo's DARK panels, never the stern face.
    const alb = s.map ? texture(s.map, uv()).rgb : vec3(0.2);
    const lum = dot(alb, vec3(0.2126, 0.7152, 0.0722));
    const dark = float(1).sub(smoothstep(0.18, 0.38, lum));
    const notStern = float(1).sub(smoothstep(0.6, 0.8, dot(n, uStern)));
    const flicker = float(0.75).add(hash2(cell.add(3.7), row.add(uSeed)).mul(0.5));
    const win = filtered.mul(dark).mul(notStern).mul(flicker).mul(uWin);
    const warm = vec3(1.0, 0.8, 0.52);
    return vec3(uTint).mul(rim).add(warm.mul(win).mul(WINDOW_EMISSION));
  })();
  hullLookState.materials++;
  return mat;
}

/**
 * Re-material a wrapped war hull for the key light. `wrap` is the wrapHull group, still PARENTLESS (its own
 * matrix is the frame above it — nose −Z, metres). Returns the minted materials (the caller disposes them at
 * deactivate). `cache` shares materials across clones of one master.
 */
export function lightWarHull(wrap: THREE.Object3D, tint: number, seed: number, cache: Map<string, THREE.Material>, windows: boolean): THREE.Material[] {
  const minted: THREE.Material[] = [];
  wrap.updateMatrixWorld(true);
  const inv = new THREE.Matrix4();
  const sc = new THREE.Vector3();
  const rot = new THREE.Matrix3();
  wrap.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const src = mesh.material as THREE.Material;
    if (!src || Array.isArray(src)) return;
    sc.setFromMatrixScale(mesh.matrixWorld);
    const mpu = (sc.x + sc.y + sc.z) / 3;
    // The stern (+Z above the wrap) in this mesh's geometry space.
    inv.copy(mesh.matrixWorld).invert();
    rot.setFromMatrix4(inv);
    const stern = new THREE.Vector3(0, 0, 1).applyMatrix3(rot).normalize();
    const key = `${src.uuid}|${tint}|${mpu.toFixed(4)}|${stern.x.toFixed(2)},${stern.y.toFixed(2)},${stern.z.toFixed(2)}|${windows ? 1 : 0}`;
    let m = cache.get(key);
    if (!m) { m = litWarMaterial(src, { tint, metersPerUnit: mpu, sternDir: stern, seed, windows }); cache.set(key, m); minted.push(m); }
    mesh.material = m;
  });
  return minted;
}

// ── nav points ──────────────────────────────────────────────────────────────────────────────────────────
const navCache = new WeakMap<object, { geo: THREE.InstancedBufferGeometry; mat: THREE.Material; count: number }>();
const uNavGain = uniform(1);

/**
 * Sample `count` surface points once per MASTER (keyed by `masterKey`): `wrap` is a parentless, recentred wrap
 * of that master (its own matrix = the frame above it). Returns a shared geometry + material; `navMesh` makes
 * the per-capital child.
 */
export function navPointSet(masterKey: object, wrap: THREE.Object3D, count: number, proud = 2.5, corridor: { axis: THREE.Vector3; from: number; to: number; n: number } | null = null): { geo: THREE.InstancedBufferGeometry; mat: THREE.Material; count: number } | null {
  const have = navCache.get(masterKey);
  if (have) return have;
  if (count <= 0) return null;
  wrap.updateMatrixWorld(true);
  const meshes: THREE.Mesh[] = [];
  wrap.traverse((o) => { if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).geometry?.getAttribute('position')) meshes.push(o as THREE.Mesh); });
  if (!meshes.length) return null;
  // Share the points across meshes by triangle area (bigger plates carry more lights).
  const areas = meshes.map((m) => { const g = m.geometry; const n = (g.index ? g.index.count : g.getAttribute('position').count) / 3; const s = new THREE.Vector3().setFromMatrixScale(m.matrixWorld); return Math.max(1, n) * s.x * s.y; });
  const total = areas.reduce((a, b) => a + b, 0);
  const pts = new Float32Array(count * 4);   // x, y, z (metres, frame above the wrap), kind
  const nrm = new THREE.Vector3(), p = new THREE.Vector3(), nm = new THREE.Matrix3();
  let k = 0;
  for (let mi = 0; mi < meshes.length && k < count; mi++) {
    const m = meshes[mi];
    const share = mi === meshes.length - 1 ? count - k : Math.round((count * areas[mi]) / total);
    if (share <= 0) continue;
    let sampler: MeshSurfaceSampler;
    try { sampler = new MeshSurfaceSampler(m).build(); } catch { continue; }
    nm.getNormalMatrix(m.matrixWorld);
    for (let j = 0; j < share && k < count; j++, k++) {
      sampler.sample(p, nrm);
      p.applyMatrix4(m.matrixWorld);
      nrm.applyMatrix3(nm).normalize();
      p.addScaledVector(nrm, proud); // proud of the plating — the near half reads, the far side is hidden by the hull
      pts[k * 4] = p.x; pts[k * 4 + 1] = p.y; pts[k * 4 + 2] = p.z;
      // Kinds: 0 port red (x<0 flanks), 1 starboard green, 2 white strobe, 3 amber running light.
      const h = ((k * 2654435761) >>> 0) / 4294967296;
      pts[k * 4 + 3] = h < 0.08 ? 2 : h < 0.55 ? 3 : (p.x < 0 ? 0 : 1);
    }
  }
  if (k === 0) return null;
  // R11: a docking corridor of CHASING lights along the approach (kind 4 + phase — a pulse runs inward).
  let all = pts.subarray(0, k * 4);
  if (corridor && corridor.n > 0) {
    const c = new Float32Array((k + corridor.n * 2) * 4);
    c.set(all);
    const ax = corridor.axis.clone().normalize();
    const side = new THREE.Vector3(0, 1, 0).cross(ax).normalize();
    if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
    for (let i = 0; i < corridor.n; i++) {
      const d = corridor.from + ((corridor.to - corridor.from) * i) / Math.max(1, corridor.n - 1);
      for (let s = 0; s < 2; s++) {
        const o = (k + i * 2 + s) * 4;
        const w = (s === 0 ? -1 : 1) * corridor.from * 0.12;
        c[o] = ax.x * d + side.x * w; c[o + 1] = ax.y * d + side.y * w; c[o + 2] = ax.z * d + side.z * w;
        c[o + 3] = 4 + ((corridor.n - 1 - i) / corridor.n) * 0.98; // phase: the pulse runs toward the berth
      }
    }
    all = c;
  }
  const quad = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setIndex(quad.getIndex());
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  geo.setAttribute('aNav', new THREE.InstancedBufferAttribute(all, 4));
  geo.instanceCount = all.length / 4;
  geo.userData.shared = true;
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  const a = attribute('aNav', 'vec4');
  const kind = a.w;
  mat.vertexNode = Fn(() => {
    const mv = modelViewMatrix.mul(vec4(a.xyz, 1.0));
    const clip = cameraProjectionMatrix.mul(mv);
    // World size 3 m, never under 1.6 px: a lamp on a 4.5 km hull stays a point at 20 km.
    const p11 = cameraProjectionMatrix.element(int(1)).y; // 1 / tan(fov/2)
    const px = max(float(1.6), float(3).mul(p11).mul(screenSize.y).mul(0.5).div(max(mv.z.negate(), float(1))));
    const off = positionGeometry.xy.mul(px.mul(2)).div(screenSize).mul(clip.w);
    return vec4(clip.xy.add(off), clip.zw);
  })();
  const t = time;
  const ph = fract(a.x.mul(0.013).add(a.z.mul(0.007)));
  const isPort = kind.lessThan(0.5), isStbd = kind.greaterThan(0.5).and(kind.lessThan(1.5));
  const isStrobe = kind.greaterThan(1.5).and(kind.lessThan(2.5));
  const strobe = pow(fract(t.add(ph)), float(40));                                  // a 1 Hz white flash
  const steady = float(0.55).add(smoothstep(0.3, 0.35, fract(t.mul(0.5).add(ph))).mul(0.45));
  const isChase = kind.greaterThan(3.5);                                             // R11's docking corridor
  const chase = pow(fract(t.mul(1.1).sub(fract(kind))), float(10)).mul(1.4).add(0.08);
  const blink = select(isChase, chase, select(isStrobe, strobe, steady));
  const col = select(isChase, vec3(0.55, 0.85, 1.0), select(isPort, vec3(1.0, 0.12, 0.08), select(isStbd, vec3(0.15, 1.0, 0.3), select(isStrobe, vec3(1, 1, 1), vec3(1.0, 0.7, 0.35)))));
  const r = length(uv().sub(0.5)).mul(2);
  const dot2 = clamp(float(1).sub(r), 0, 1);
  mat.colorNode = col.mul(select(isStrobe, float(9), float(4.5))).mul(blink).mul(uNavGain);
  mat.opacityNode = dot2.mul(dot2);
  mat.name = 'space-nav-points';
  markMrtOverlay(mat);
  mat.userData.shared = true;
  const set = { geo, mat, count: k };
  navCache.set(masterKey, set);
  hullLookState.navMasters++;
  hullLookState.navPoints += k;
  return set;
}

/** One capital's nav-point child (shares the side's geometry + material). */
export function navMesh(set: { geo: THREE.InstancedBufferGeometry; mat: THREE.Material }): THREE.Mesh {
  const m = new THREE.Mesh(set.geo, set.mat);
  m.frustumCulled = false;
  m.name = 'space-nav';
  m.renderOrder = 7;
  return m;
}

/** Probe lever: nav-point gain (0 hides them for an A/B frame). */
export function setNavGain(v: number): void { (uNavGain as unknown as { value: number }).value = v; }

